import { useState, memo, useCallback, useMemo } from "react";
import { useForm, FieldValues } from "react-hook-form";
import { toast } from "react-hot-toast";
import { BaseButton } from "components/buttons/BaseButton";
import { ImageUploader } from "components/UI/ImageUploader";
import { NicheInputField } from "components/niches/NicheInputField";
import "./NicheForm.scss";

export interface NicheFields {
  id: "name" | "description" | "rules";
  label: string;
  placeholder: string;
  errorMessage: string;
  type?: string;
  rows?: number;
  disabled?: boolean;
  value: string;
}

export enum NicheFieldsNames {
  name="name",
  description="description",
  rules="rules"
}

interface INicheFormProps {
  ticketId: string | null;
  onSubmit: (data: FieldValues) => void;
  handleTicketId: (ticketId: string) => void;
  fields: { [key in NicheFieldsNames]: NicheFields };
  btnTitle: string;
  preview?: string | null;
}

export const NicheForm = memo((props: INicheFormProps) => {
  const {
    onSubmit,
    handleTicketId,
    ticketId,
    fields,
    btnTitle,
    preview
  } = props;

  const [imgLoading, setImgLoading] = useState<number>(0);

  const {
    register,
    handleSubmit,
    formState: { errors, isValid, isSubmitting }
  } = useForm({
    mode: "all",
    defaultValues: {
      name: fields[NicheFieldsNames.name].value,
      description: fields[NicheFieldsNames.description].value,
      rules: fields[NicheFieldsNames.rules].value
    }
  });

  const handleImageError = useCallback((message: string) => {
    handleTicketId("");
    toast.error(message, { duration: 5000 });
  }, [handleTicketId]);

  const handleImageLoad = (percent: number) => {
    setImgLoading(percent);
  };

  const isButtonDisabled = useMemo(() => {
    return !isValid || isSubmitting || (preview && imgLoading > 0) || (!preview && (imgLoading > 0 || !ticketId));
  }, [isValid, isSubmitting, preview, imgLoading, ticketId]);

  return (
    <form className="NicheForm" onSubmit={handleSubmit(onSubmit)}>
      <div className="NicheImage">
        <ImageUploader
          preview={preview || undefined}
          title="Choose an image"
          onReady={handleTicketId}
          onError={handleImageError}
          onProgress={handleImageLoad}
        />
      </div>

      {Object.values(fields).map((fieldOptions) => (
        <NicheInputField
          key={fieldOptions.id}
          errors={errors}
          register={register}
          {...fieldOptions}
        />
      ))}

      <div className="BtnWrap">
        <BaseButton type="submit" disabled={!!isButtonDisabled}>
          {isSubmitting ? "Uploading..." : btnTitle}
        </BaseButton>
      </div>
    </form>
  );
});

